import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { api, API_BASE } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import Spinner from "@/components/Spinner";
import AdBanner from "@/components/AdBanner";
import type { DealItem, SearchResult, ChartsData } from "@/types/api";

const PAGE_SIZE = 50;

export default function StorePage() {
  const { t } = useLang();
  const [games, setGames] = useState<DealItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const fetchingRef = useRef(false);

  // Filters
  const [sortBy, setSortBy] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [gamesOnly, setGamesOnly] = useState(false);
  const [hideF2P, setHideF2P] = useState(false);
  const [koreanOnly, setKoreanOnly] = useState(false);
  const [onSaleOnly, setOnSaleOnly] = useState(false);

  // Client filters
  const [minDiscount, setMinDiscount] = useState(0);
  const [reviewFilter, setReviewFilter] = useState<"all" | "positive" | "mixedPlus">("all");
  const [platformWin, setPlatformWin] = useState(false);
  const [platformMac, setPlatformMac] = useState(false);
  const [platformLinux, setPlatformLinux] = useState(false);

  // Charts for stat cards
  const { data: charts } = useFetch<ChartsData>(() => api.charts("ko") as Promise<ChartsData>, []);

  const buildQuery = useCallback((start: number) => {
    const p = new URLSearchParams();
    p.set("start", String(start));
    p.set("count", String(PAGE_SIZE));
    if (sortBy) p.set("sort_by", sortBy);
    if (maxPrice) p.set("maxprice", maxPrice);
    if (searchQuery) p.set("query", searchQuery);
    if (gamesOnly) p.set("category1", "998");
    if (hideF2P) p.set("hidef2p", "1");
    if (koreanOnly) p.set("supportedlang", "koreana");
    if (onSaleOnly) p.set("specials", "1");
    p.set("lang", "ko");
    return `${API_BASE}/store/search?${p.toString()}`;
  }, [sortBy, maxPrice, searchQuery, gamesOnly, hideF2P, koreanOnly, onSaleOnly]);

  const fetchGames = useCallback(async (start: number, append: boolean) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (!append) setLoading(true); else setLoadingMore(true);
    try {
      const res = await fetch(buildQuery(start));
      const data: SearchResult = await res.json();
      if (append) {
        setGames((prev) => { const ids = new Set(prev.map((d) => d.appid)); return [...prev, ...data.results.filter((d) => !ids.has(d.appid))]; });
      } else {
        setGames(data.results);
        setTotalCount(data.total_count);
      }
    } catch { /* */ }
    setLoading(false);
    setLoadingMore(false);
    fetchingRef.current = false;
  }, [buildQuery]);

  useEffect(() => { setPage(0); fetchGames(0, false); }, [fetchGames]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const bottom = document.documentElement.scrollHeight - window.scrollY - window.innerHeight;
      if (bottom < 400 && !fetchingRef.current && games.length < totalCount) {
        const next = (page + 1) * PAGE_SIZE;
        setPage((p) => p + 1);
        fetchGames(next, true);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [page, games.length, totalCount, fetchGames]);

  // Client filters
  const filteredGames = useMemo(() => {
    let f = games;
    if (minDiscount > 0) f = f.filter((d) => parseInt(d.discount_percent) >= minDiscount);
    if (reviewFilter === "positive") f = f.filter((d) => d.review_summary === "positive");
    else if (reviewFilter === "mixedPlus") f = f.filter((d) => d.review_summary === "positive" || d.review_summary === "mixed");
    if (platformWin) f = f.filter((d) => d.platforms?.windows);
    if (platformMac) f = f.filter((d) => d.platforms?.mac);
    if (platformLinux) f = f.filter((d) => d.platforms?.linux);
    return f;
  }, [games, minDiscount, reviewFilter, platformWin, platformMac, platformLinux]);

  const handleSearch = () => setSearchQuery(searchInput);

  const toggleBtn = (active: boolean) =>
    active ? "bg-gray-900 text-white" : "bg-inner-bg dark:bg-inner-bg text-gray-600 dark:text-muted border border-gray-200 dark:border-card-border hover:bg-hover-bg dark:hover:bg-hover-bg";

  return (
    <div className="max-w-[1320px] mx-auto px-4 sm:px-6 flex-1 w-full">
      {/* Header description */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm p-4 sm:p-5 mt-6 mb-6">
        <div className="flex items-center gap-2.5 mb-2">
          <div className="w-8 h-8 rounded-lg bg-gray-900 dark:bg-card-border flex items-center justify-center">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
          </div>
          <h1 className="text-sm sm:text-base font-bold text-gray-900 dark:text-white">{t.store?.title || "스토어"}</h1>
        </div>
        <p className="text-xs sm:text-sm text-gray-500 dark:text-muted leading-relaxed">{t.store?.description || "Steam 게임을 검색하고 필터링하세요."}</p>
      </div>

      {/* Stats */}
      {!loading && totalCount > 0 && !searchQuery && (
        <div className="pt-2 pb-4">
          <div className="text-center mb-6">
            <p className="text-sm font-medium text-gray-500 dark:text-muted mb-1">{t.store?.allGames || "전체 게임"}</p>
            <p className="text-3xl sm:text-5xl font-black text-gray-900 dark:text-white tabular-nums">
              {totalCount.toLocaleString("ko-KR")}
              <span className="text-sm sm:text-lg font-medium text-gray-400 dark:text-muted ml-1.5">{t.common?.games || "개"}</span>
            </p>
          </div>

          {/* Chart stat cards */}
          {charts && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              {[
                { games: charts.most_played, label: t.home?.mostPlayed || "최다 플레이", accent: "text-emerald-500", badgeBg: "bg-emerald-500" },
                { games: charts.hot_releases, label: t.home?.hotReleases || "핫 신작", accent: "text-orange-500", badgeBg: "bg-orange-500" },
                { games: charts.trending, label: t.home?.trending || "트렌딩", accent: "text-blue-500", badgeBg: "bg-blue-500" },
              ].map(({ games: chartGames, label, accent, badgeBg }) => {
                const game = chartGames?.[0];
                if (!game) return null;
                return (
                  <Link key={label} to={`/game/${game.appid}`} className="group bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all duration-200">
                    <div className="relative aspect-[46/21] bg-hover-bg dark:bg-background overflow-hidden">
                      <img src={game.header_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.appid}/header.jpg`} alt={game.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                      <span className={`absolute top-3 left-3 ${badgeBg} text-white text-[11px] font-bold px-2 py-0.5 rounded-md`}>{label}</span>
                    </div>
                    <div className="p-4">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white line-clamp-1 mb-1 group-hover:text-blue-600 transition-colors">{game.name}</p>
                      <p className={`text-xs font-bold ${accent}`}>{game.price || (game.is_free ? (t.common?.free || "무료") : "")}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Search + Filters — DealsPage와 동일한 UI */}
      <div className="bg-card-bg dark:bg-card-bg rounded-2xl border border-gray-200 dark:border-card-border shadow-sm mb-5 overflow-hidden">
        {/* Search */}
        <div className="p-4 border-b border-inner-border dark:border-card-border">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-muted" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
              <input type="text" placeholder={t.deals?.searchPlaceholder || "게임 이름 검색..."} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} className="w-full bg-inner-bg dark:bg-inner-bg border border-gray-200 dark:border-card-border rounded-xl pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-sub-text focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all" />
            </div>
            <button onClick={handleSearch} className="bg-gray-900 hover:bg-gray-800 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-colors active:scale-[0.98]">{t.common?.search || "검색"}</button>
            {searchQuery && <button onClick={() => { setSearchInput(""); setSearchQuery(""); }} className="text-sm text-gray-400 hover:text-gray-700 px-2">{t.common?.reset || "초기화"}</button>}
          </div>
        </div>

        {/* Sort + Quick filters */}
        <div className="px-4 py-3 flex flex-wrap items-center gap-2.5">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-inner-bg dark:bg-inner-bg border border-gray-200 dark:border-card-border rounded-lg px-3 py-1.5 text-sm text-gray-700 dark:text-foreground focus:outline-none focus:border-blue-400 cursor-pointer">
            <option value="">{t.deals?.sortPopular || "인기순"}</option>
            <option value="Price_ASC">{t.deals?.sortPriceLow || "가격 낮은순"}</option>
            <option value="Price_DESC">{t.deals?.sortPriceHigh || "가격 높은순"}</option>
            <option value="Reviews_DESC">{t.deals?.sortRating || "평점순"}</option>
            <option value="Released_DESC">{t.deals?.sortNewest || "최신순"}</option>
            <option value="Name_ASC">{t.deals?.sortName || "이름순"}</option>
          </select>
          <select value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="bg-inner-bg dark:bg-inner-bg border border-gray-200 dark:border-card-border rounded-lg px-3 py-1.5 text-sm text-gray-700 dark:text-foreground focus:outline-none focus:border-blue-400 cursor-pointer">
            <option value="">{t.deals?.allPrices || "전체 가격"}</option>
            <option value="5000">₩5,000 이하</option>
            <option value="10000">₩10,000 이하</option>
            <option value="20000">₩20,000 이하</option>
            <option value="30000">₩30,000 이하</option>
            <option value="50000">₩50,000 이하</option>
          </select>
          <div className="h-5 w-px bg-gray-200 dark:bg-card-border mx-0.5" />
          <button onClick={() => setOnSaleOnly(!onSaleOnly)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${toggleBtn(onSaleOnly)}`}>{t.store?.onSaleOnly || "할인만"}</button>
          <button onClick={() => setGamesOnly(!gamesOnly)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${toggleBtn(gamesOnly)}`}>{t.deals?.gamesOnly || "게임만"}</button>
          <button onClick={() => setHideF2P(!hideF2P)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${toggleBtn(hideF2P)}`}>{t.deals?.excludeF2P || "F2P 제외"}</button>
          <button onClick={() => setKoreanOnly(!koreanOnly)} className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${toggleBtn(koreanOnly)}`}>{t.deals?.koreanSupport || "한국어"}</button>
        </div>

        {/* Advanced filters */}
        <div className="px-4 py-3 border-t border-inner-border dark:border-card-border flex flex-wrap items-center gap-2.5">
          <span className="text-xs text-gray-400 dark:text-sub-text font-medium">할인율</span>
          {[25, 50, 75, 90].map((d) => (
            <button key={d} onClick={() => setMinDiscount(minDiscount === d ? 0 : d)} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${toggleBtn(minDiscount === d)}`}>{d}%+</button>
          ))}
          <div className="h-5 w-px bg-gray-200 dark:bg-card-border mx-0.5" />
          <span className="text-xs text-gray-400 dark:text-sub-text font-medium">{t.common?.rating || "평가"}</span>
          <button onClick={() => setReviewFilter("all")} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${toggleBtn(reviewFilter === "all")}`}>{t.deals?.reviewAll || "전체"}</button>
          <button onClick={() => setReviewFilter("positive")} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${toggleBtn(reviewFilter === "positive")}`}>{t.deals?.reviewPositive || "긍정적만"}</button>
          <button onClick={() => setReviewFilter("mixedPlus")} className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${toggleBtn(reviewFilter === "mixedPlus")}`}>{t.deals?.reviewMixedPlus || "복합적 이상"}</button>
          <div className="h-5 w-px bg-gray-200 dark:bg-card-border mx-0.5" />
          <span className="text-xs text-gray-400 dark:text-sub-text font-medium">{t.gameDetail?.platformLabel || "플랫폼"}</span>
          <button onClick={() => setPlatformWin(!platformWin)} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${toggleBtn(platformWin)}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M3 12.5l7-1V4L3 5.5v7zm0 7L10 21v-7.5l-7-1v7zm8-1L21 21V12h-10v6.5zm0-14v7.5H21V3L11 4.5z" /></svg>Windows
          </button>
          <button onClick={() => setPlatformMac(!platformMac)} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${toggleBtn(platformMac)}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" /></svg>Mac
          </button>
          <button onClick={() => setPlatformLinux(!platformLinux)} className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${toggleBtn(platformLinux)}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12.504 0c-.155 0-.311.015-.466.045C9.49.401 7.803 2.926 7.803 5.594c0 .724.147 1.44.436 2.119.294.687.707 1.293 1.228 1.8.112.108.224.215.337.321v3.854H7.803v1.5h2.341v3.854c-.113.104-.225.213-.337.321-.521.507-.934 1.113-1.228 1.8-.289.679-.436 1.395-.436 2.119 0 2.668 1.687 5.193 4.235 5.549.155.03.311.045.466.045s.311-.015.466-.045c2.548-.356 4.235-2.881 4.235-5.549 0-.724-.147-1.44-.436-2.119-.294-.687-.707-1.293-1.228-1.8-.112-.108-.224-.215-.337-.321v-3.854h2.341v-1.5H14.14V9.884c.113-.106.225-.213.337-.321.521-.507.934-1.113 1.228-1.8.289-.679.436-1.395.436-2.119 0-2.668-1.687-5.193-4.235-5.549A3.612 3.612 0 0012.504 0z" /></svg>Linux
          </button>
        </div>
      </div>

      {/* Game list */}
      {loading ? (
        <div className="space-y-1.5">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton h-[52px] rounded-xl" />)}</div>
      ) : filteredGames.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-400 dark:text-sub-text">{t.deals?.noResults || "게임이 없어요."}</div>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            {filteredGames.map((game, idx) => (
              <div key={`${game.appid}-${idx}`}>
              {idx > 0 && idx % 10 === 0 && <AdBanner className="my-2" />}
              <Link to={`/game/${game.appid}`} className="flex items-center gap-2 px-3 py-2 bg-card-bg rounded-xl border border-gray-200 dark:border-card-border hover:border-gray-300 dark:hover:border-card-border hover:shadow-md transition-all group">
                <div className="relative shrink-0 w-[80px] h-[37px] sm:w-[120px] sm:h-[56px] rounded-lg overflow-hidden bg-hover-bg dark:bg-background">
                  <img src={game.capsule_image || `https://shared.cloudflare.steamstatic.com/store_item_assets/steam/apps/${game.appid}/header.jpg`} alt={game.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                </div>
                <div className="flex-1 min-w-0 flex items-center gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[11px] sm:text-sm text-gray-900 dark:text-white group-hover:text-blue-600 transition-colors line-clamp-1">{game.name}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      {game.review_summary && game.review_summary !== "none" && <span className="text-[9px] text-gray-400 dark:text-muted">{game.review_summary === "positive" ? (t.common?.positive || "긍정적") : game.review_summary === "mixed" ? (t.common?.mixed || "복합적") : (t.common?.negative || "부정적")}</span>}
                      {game.release_date && <span className="text-[9px] text-gray-400 dark:text-sub-text hidden sm:inline">{game.release_date}</span>}
                      <div className="flex items-center gap-1">
                        {game.platforms?.windows && <span className="text-[8px] text-gray-300 dark:text-sub-text">Win</span>}
                        {game.platforms?.mac && <span className="text-[8px] text-gray-300 dark:text-sub-text">Mac</span>}
                        {game.platforms?.linux && <span className="text-[8px] text-gray-300 dark:text-sub-text">Lin</span>}
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 text-right flex items-center gap-2">
                    {Number(game.discount_percent) > 0 && <span className="text-[10px] sm:text-xs font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-500/20 px-1.5 py-0.5 rounded">-{game.discount_percent}%</span>}
                    <div>
                      {game.original_price_text && Number(game.discount_percent) > 0 && <p className="text-[9px] text-gray-400 line-through">{game.original_price_text}</p>}
                      <p className={`text-[11px] sm:text-sm font-bold ${Number(game.discount_percent) > 0 ? "text-green-600 dark:text-green-400" : "text-gray-700 dark:text-gray-200"}`}>{game.final_price_text || (t.common?.free || "무료")}</p>
                    </div>
                  </div>
                </div>
              </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-[10px] text-gray-400 dark:text-sub-text py-2">{filteredGames.length.toLocaleString("ko-KR")} / {totalCount.toLocaleString("ko-KR")}</p>
        </>
      )}

      {loadingMore && <Spinner />}
      <AdBanner className="my-6" />
    </div>
  );
}
